import { Alert, Button, Radio, Spin } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { subManagementService } from "@/api/subManagementService";
import { Breadcrumb, ProTable, ZhMessageBox, type ProColumn } from "@/components/common";

interface SmsSetting {
    id?: number;
    name?: string;
    phone?: string;
    receiptSms?: boolean | number;
    [key: string]: unknown;
}
/** 短信设置编辑页允许逐账号修改接收状态并批量保存。 */
export function SmsManagementEditPage() {
    const [rows, setRows] = useState<SmsSetting[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [saveOpen, setSaveOpen] = useState(false);
    const navigate = useNavigate();
    /** 编辑页始终加载全部主账号及子账号配置。 */
    const load = async () => {
        setLoading(true);
        setError(false);
        try {
            const data = await subManagementService.getCustomerAndSub({});
            setRows((data.customerSmsVos ?? []) as unknown as SmsSetting[]);
        } catch {
            setError(true);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        void load();
    }, []);
    /** 只替换目标行的短信标记，保留后端返回的其它业务字段。 */
    const changeReceipt = (index: number, value: number) =>
        setRows(current => current.map((row, rowIndex) => (rowIndex === index ? { ...row, receiptSms: value } : row)));
    /** 按旧 confirmWrapper 语义保存，接口成功后返回上一页。 */
    const save = async () => {
        await subManagementService.getCustomerAndSubSave(rows as any);
        setSaveOpen(false);
        navigate(-1);
    };
    const columns = useMemo<ProColumn<SmsSetting>[]>(
        () => [
            {
                key: "name",
                title: "姓名",
                minWidth: 142,
                search: {
                    key: "insititutionName",
                    type: "input",
                    placeholder: "请输入姓名/手机号",
                    span: 5,
                    width: 120
                },
                render: (_, row) => row.name || "--"
            },
            { key: "phone", title: "手机号", minWidth: 142, render: (_, row) => row.phone || "--" },
            {
                key: "receiptSms",
                title: "是否接收短信",
                minWidth: 142,
                render: (value, _row, index) => (
                    <Radio.Group value={Number(value)} onChange={event => changeReceipt(index, event.target.value)}>
                        <Radio value={1}>是</Radio>
                        <Radio value={0}>否</Radio>
                    </Radio.Group>
                )
            }
        ],
        []
    );
    if (loading) return <Spin fullscreen tip="短信设置加载中" />;
    if (error)
        return (
            <Alert
                type="error"
                showIcon
                message="短信设置加载失败"
                action={<Button onClick={() => void load()}>重试</Button>}
            />
        );
    return (
        <section className="sms-management-edit-page">
            <div className="section-card sms-management-edit-card">
                <Breadcrumb
                    routes={[
                        { name: "账号管理" },
                        { name: "短信管理", path: "/me/SMSManagement" },
                        { name: "短信管理" }
                    ]}
                />
                <div className="sms-management-edit-table">
                    <ProTable
                        title="短信管理列表"
                        rowKey={row => row.id ?? row.phone ?? row.name ?? "unknown"}
                        pagination={false}
                        columns={columns}
                        data={rows}
                    />
                </div>
            </div>
            <div className="sms-management-edit-footer">
                <Button type="primary" onClick={() => setSaveOpen(true)}>
                    保存
                </Button>
                <Button type="primary" onClick={() => navigate(-1)}>
                    返回
                </Button>
            </div>
            <ZhMessageBox open={saveOpen} tip="是否保存" onCancel={() => setSaveOpen(false)} onSubmit={save} />
        </section>
    );
}
